import { gql, useQuery } from "@apollo/client";
import Errors from "components/base/Errors";
import { Select } from "components/base/Form";
import SearchBar from "components/base/SearchBar";
import Spinner from "components/base/Spinner";
import Button from "components/base/Button";
import moment from "moment";
import { useState } from "react";
import { useParams } from "react-router-dom";
import XLSX from "xlsx";

const FETCH_COMPARE_STOCK_DATA = gql`
  query FETCH_COMPARE_STOCK_DATA($name: String!) {
    compareOdooStock(name: $name)
  }
`;

const CompareWithOdoo = () => {
  const { name } = useParams();
  const { loading, error, data } = useQuery(FETCH_COMPARE_STOCK_DATA, {
    variables: { name: "ALC-" + name },
  });
  const [filter, setFilter] = useState("all");
  const [searchText, setSearchText] = useState("");

  const is2C = name.toUpperCase().includes("2C");

  const parseQty = (value) => {
    if (value === null || value === undefined || value === "") {
      return { value: null, invalid: true };
    }

    const num = Number(value);
    if (Number.isFinite(num)) {
      return { value: num, invalid: false };
    }

    return { value: null, invalid: true };
  };

  const getRowValues = (item) => {
    const odoo = parseQty(item.odoo_qty);
    const alc = parseQty(item.alc_qty);
    const shipped = parseQty(item.shipped_qty);
    const hasInvalidQty = odoo.invalid || alc.invalid || (is2C && shipped.invalid);

    return {
      odooQty: odoo.value,
      alcQty: alc.value,
      shippedQty: shipped.value,
      hasInvalidQty,
      diff: hasInvalidQty ? null : is2C ? odoo.value - alc.value - shipped.value : odoo.value - alc.value,
    };
  };

  const calculateDiff = (item) => {
    return getRowValues(item).diff;
  };

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const res = JSON.parse(data.compareOdooStock);
  const totalCount = res.length;
  const differentCount = res.filter((i) => calculateDiff(i) !== 0).length;
  const sameCount = res.filter((i) => calculateDiff(i) === 0).length;

  const resToShow = res
    .filter((i) => {
      if (filter === "all") return true;
      if (filter === "different") return calculateDiff(i) !== 0;
      if (filter === "same") return calculateDiff(i) === 0;
    })
    .filter((i) => {
      const cleanedSearchText = searchText
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
      return (
        i.number.toLowerCase().includes(cleanedSearchText) ||
        i.name.toLowerCase().includes(cleanedSearchText)
      );
    });

  function exportExcel() {
    const data = resToShow.map((i) => {
      const { odooQty, alcQty, shippedQty, diff } = getRowValues(i);
      const baseData = {
        Item: i.number,
        "Odoo QTY": odooQty ?? "INVALID",
        "ALC QTY": alcQty ?? "INVALID",
      };

      if (is2C) {
        return {
          ...baseData,
          "Shipped QTY": shippedQty ?? "INVALID",
          "Diff (Odoo - ALC - Shipped)": diff ?? "INVALID",
        };
      } else {
        return {
          ...baseData,
          "Diff (Odoo - ALC)": diff ?? "INVALID",
        };
      }
    });
    const sheet = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, "stock");
    const today = moment().format("YYYY-MM-DD");
    XLSX.writeFile(wb, `ALC vs Odoo ${today}.xlsx`);
  }

  return (
    <div className="p-6 flex flex-col flex-1 overflow-auto">
      <div className="opacity-70">
        This page compare the inventory between ALC and Odoo NL Warehouse. There
        are some item numbers different between this two systems(for example:
        302C01_A and 302C01-A), we setup some configuration(ignore -, _, case
        insensitive, auto correct some numbers, etc) so that it can auto compute
        most items.
        {is2C && (
          <>
            The "Shipped QTY" column shows quantities that have already been
            shipped in current month, and "Diff (Odoo - ALC - Shipped)" shows
            the effective inventory difference after accounting for both ALC
            inventory and shipped quantities. If this difference is positive, it
            indicates potential ungenrated shipments from ALC. If it's negative,
            the stock of this product may have been restocked.
          </>
        )}
        {!is2C && (
          <>
            If Odoo has more qty than ALC, probably there are some Sales Orders
            not delivered. If ALC has more qty than Odoo, there are some manual
            work need to be done to adjust.
          </>
        )}
      </div>

      <div className="flex items-center justify-between mt-4 space-x-6">
        <div className="flex items-center space-x-6 flex-1">
          <Select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All Products({totalCount})</option>
            <option value="different">
              Only Products with different stock({differentCount})
            </option>
            <option value="same">
              Only Products with the same stock({sameCount})
            </option>
          </Select>
          <div className="w-full">
            <SearchBar
              placeholder="Search by item number or name"
              value={searchText}
              onChange={setSearchText}
            />
          </div>
          <Button onClick={exportExcel}>Export(Excel)</Button>
        </div>

        <a
          href={`${process.env.REACT_APP_SERVER_HOST}/static/alc-stock.xml`}
          target="_blank"
          rel="noreferer"
        >
          Donwload Original Report from ALC
        </a>
      </div>

      <div className="card mt-4 p-0 pb-1 whitespace-nowrap flex-1 overflow-auto">
        <table>
          <thead>
            <tr className="sticky top-0 z-10 backdrop-blur dark:bg-gray-800 dark:bg-opacity-80">
              <th className="px-6 py-3">Item</th>
              <th className="px-6">Name(ALC)</th>
              <th className="text-right px-6">Odoo QTY</th>
              <th className="text-right px-6">ALC QTY</th>
              {is2C && <th className="text-right px-6">Shipped QTY</th>}
              <th className="text-right px-6">
                Diff ({is2C ? "Odoo - ALC - Shipped" : "Odoo - ALC"})
              </th>
            </tr>
          </thead>
          <tbody>
            {resToShow.map((i, index) => {
              const { odooQty, alcQty, shippedQty, hasInvalidQty, diff } =
                getRowValues(i);
              const rowClass =
                hasInvalidQty
                  ? "bg-yellow-100 dark:bg-yellow-800"
                  : diff > 0
                  ? "bg-red-100 dark:bg-red-800 "
                  : diff < 0
                    ? "bg-green-100 dark:bg-green-800"
                    : "";

              return (
                <tr
                  key={index}
                  className={`border-t border-gray-100 dark:border-gray-700 dark:bg-opacity-40 ${rowClass}`}
                >
                  <td className="px-6">{i.number}</td>
                  <td className="px-6">{i.name}</td>
                  <td className="text-right px-6">{odooQty ?? "--"}</td>
                  <td className="text-right px-6">{alcQty ?? "--"}</td>
                  {is2C && <td className="text-right px-6">{shippedQty ?? "--"}</td>}
                  <td className="text-right px-6">{diff ?? "--"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CompareWithOdoo;
